import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../api';
import logoMark from '../assets/images/logo.jpg';
import AdminBillingPanel from '../billing/AdminBillingPanel';

const YEAR_LABELS = {
  1: '1st Year (B.Tech)', 2: '2nd Year (B.Tech)', 3: '3rd Year (B.Tech)',
  4: '4th Year (B.Tech)', 5: '5th Year (Dual/Integrated)',
  6: 'M.Tech / M.S. 1st Year', 7: 'M.Tech / M.S. 2nd Year',
  8: 'PhD Scholar', 9: 'MBA 1st Year', 10: 'MBA 2nd Year'
};

function yearLabel(year) {
  return year ? (YEAR_LABELS[year] || `Year ${year}`) : null;
}

const STATUS_STYLE = {
  Verified:     { bg: '#d1fae5', color: '#065f46' },
  Pending:      { bg: '#fef3c7', color: '#92400e' },
  Rejected:     { bg: '#fee2e2', color: '#991b1b' },
  'Under Review': { bg: '#dbeafe', color: '#1e40af' },
  Suspended:    { bg: '#f3f4f6', color: '#374151' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Pending;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 12, fontWeight: 600,
      padding: '4px 12px', borderRadius: 20, flexShrink: 0, alignSelf: 'flex-start'
    }}>{status || 'Pending'}</span>
  );
}

function StudentModal({ student, onClose, onVerify, actionLoading, readOnly }) {
  if (!student) return null;

  const resumeUrl = student.resume_url
    ? (student.resume_url.startsWith('http')
        ? student.resume_url
        : `${API_URL}${student.resume_url.replace('/uploads/', '/api/uploads/')}`)
    : null;

  const photoUrl = student.profile_photo
    ? (student.profile_photo.startsWith('http') ? student.profile_photo : `${API_URL}${student.profile_photo}`)
    : null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)', borderRadius: 16, padding: '32px',
          width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)', position: 'relative'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16, background: 'none',
            border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)',
            lineHeight: 1, padding: '4px 8px', borderRadius: 6
          }}
        >×</button>

        {/* Header */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 24 }}>
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={student.name}
              style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid var(--border)' }}
            />
          ) : (
            <div style={{
              width: 72, height: 72, borderRadius: '50%', background: 'var(--primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, color: '#fff', fontWeight: 700, flexShrink: 0
            }}>
              {student.name?.[0]?.toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 22 }}>{student.name}</h2>
            <div style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>{student.email}</div>
            {student.phone && <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>{student.phone}</div>}
          </div>
          <StatusBadge status={student.screening_status || (readOnly ? 'Verified' : 'Pending')} />
        </div>

        {/* Academic */}
        <Section title="Academic Info">
          <Row label="College" value={student.college || student.iit_campus} />
          <Row label="Department" value={student.department} />
          <Row label="Year" value={yearLabel(student.year)} />
        </Section>

        {/* Bio */}
        {student.bio && (
          <Section title="Bio">
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{student.bio}</p>
          </Section>
        )}

        {/* Skills */}
        {student.skills?.length > 0 && (
          <Section title="Skills">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {student.skills.map(s => (
                <span key={s._id || s} style={{
                  background: 'var(--primary-light)', color: 'var(--primary)',
                  fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 20
                }}>
                  {s.name || s}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* Work Preferences */}
        <Section title="Work Preferences">
          {student.work_mode?.length > 0 && (
            <Row label="Work Mode" value={student.work_mode.join(', ')} />
          )}
          {student.availability?.length > 0 && (
            <Row label="Availability" value={student.availability.join(', ')} />
          )}
          {student.weekly_commitment && (
            <Row label="Weekly Hours" value={`${student.weekly_commitment} hrs/week`} />
          )}
          {(student.monthly_rate_min || student.monthly_rate_max) && (
            <Row
              label="Expected Stipend"
              value={`₹${(student.monthly_rate_min || 0).toLocaleString()} – ₹${(student.monthly_rate_max || 0).toLocaleString()} / month`}
            />
          )}
          {student.notice_period && (
            <Row label="Notice Period" value={student.notice_period} />
          )}
        </Section>

        {/* Links */}
        <Section title="Links">
          {resumeUrl && (
            <div style={{ marginBottom: 8 }}>
              <a href={resumeUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontSize: 14, textDecoration: 'none', fontWeight: 500 }}>
                View Resume →
              </a>
            </div>
          )}
          {student.portfolio_url && (
            <div style={{ marginBottom: 8 }}>
              <a href={student.portfolio_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontSize: 14, textDecoration: 'none' }}>
                Portfolio →
              </a>
            </div>
          )}
          {student.linkedin_url && (
            <div style={{ marginBottom: 8 }}>
              <a href={student.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontSize: 14, textDecoration: 'none' }}>
                LinkedIn →
              </a>
            </div>
          )}
          {student.github_url && (
            <div>
              <a href={student.github_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontSize: 14, textDecoration: 'none' }}>
                GitHub →
              </a>
            </div>
          )}
          {!resumeUrl && !student.portfolio_url && !student.linkedin_url && !student.github_url && (
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>No links provided</p>
          )}
        </Section>

        {/* Joined */}
        {student.created_at && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
            Registered: {new Date(student.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        )}

        {/* Actions */}
        {!readOnly && (
          <div style={{ display: 'flex', gap: 12, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
            <button
              className="btn btn-success"
              style={{ flex: 1 }}
              onClick={() => onVerify(student._id, 'Verified')}
              disabled={actionLoading === student._id + 'Verified'}
            >
              {actionLoading === student._id + 'Verified' ? 'Approving…' : 'Approve Student'}
            </button>
            <button
              className="btn btn-danger"
              style={{ flex: 1 }}
              onClick={() => onVerify(student._id, 'Rejected')}
              disabled={actionLoading === student._id + 'Rejected'}
            >
              {actionLoading === student._id + 'Rejected' ? 'Rejecting…' : 'Reject Student'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 14 }}>
      <span style={{ color: 'var(--text-muted)', minWidth: 110, flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

const JOB_STATUS_STYLE = {
  published: { bg: '#d1fae5', color: '#065f46' },
  draft:     { bg: '#fef3c7', color: '#92400e' },
  closed:    { bg: '#fee2e2', color: '#991b1b' },
};

function JobStatusBadge({ status }) {
  const s = JOB_STATUS_STYLE[status] || JOB_STATUS_STYLE.draft;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontSize: 12, fontWeight: 600,
      padding: '3px 10px', borderRadius: 20, display: 'inline-block'
    }}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Draft'}
    </span>
  );
}

function ApplicantsModal({ job, applicants, loading, onClose }) {
  if (!job) return null;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)', borderRadius: 16, padding: '32px',
          width: '100%', maxWidth: 760, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)', position: 'relative'
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16, background: 'none',
            border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)',
            lineHeight: 1, padding: '4px 8px', borderRadius: 6
          }}
        >×</button>

        <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>Applicants</h2>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24 }}>
          {job.title}
          {!loading && (
            <span style={{
              marginLeft: 10, background: 'var(--primary)', color: '#fff',
              fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20
            }}>
              {applicants.length} applicant{applicants.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}><div className="spinner" /></div>
        ) : applicants.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            No applications yet for this job.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>College</th>
                  <th>Year / Dept</th>
                  <th>Status</th>
                  <th>Applied</th>
                  <th>Links</th>
                </tr>
              </thead>
              <tbody>
                {applicants.map(app => {
                  const s = app.student_id;
                  if (!s) return null;
                  const resumeUrl = s.resume_url
                    ? (s.resume_url.startsWith('http') ? s.resume_url : `${API_URL}${s.resume_url.replace('/uploads/', '/api/uploads/')}`)
                    : null;
                  return (
                    <tr key={app._id}>
                      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{s.name}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{s.email}</td>
                      <td style={{ fontSize: 13 }}>{s.college || s.iit_campus || '—'}</td>
                      <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                        {s.year ? `Year ${s.year}` : '—'}
                        {s.department ? ` · ${s.department}` : ''}
                      </td>
                      <td>
                        <span style={{
                          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                          background: app.status === 'Selected' ? '#d1fae5'
                            : app.status === 'Rejected' ? '#fee2e2'
                            : app.status === 'Shortlisted' ? '#dbeafe'
                            : '#fef3c7',
                          color: app.status === 'Selected' ? '#065f46'
                            : app.status === 'Rejected' ? '#991b1b'
                            : app.status === 'Shortlisted' ? '#1e40af'
                            : '#92400e',
                        }}>{app.status}</span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {new Date(app.applied_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {resumeUrl && (
                            <a href={resumeUrl} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                              Resume
                            </a>
                          )}
                          {s.linkedin_url && (
                            <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                              LinkedIn
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function JobEditModal({ job, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    title: job.title || '',
    status: job.status || 'draft',
    visibility: job.visibility || 'public',
    role_category: job.role_category || 'Engineering',
    position_type: job.position_type || 'Full-time',
    work_mode: job.work_mode || 'Remote',
    location: job.location || '',
    stipend_min: job.stipend?.min ?? '',
    stipend_max: job.stipend?.max ?? '',
    num_openings: job.num_openings || 1,
    application_deadline: job.application_deadline
      ? new Date(job.application_deadline).toISOString().split('T')[0]
      : '',
    description: job.description || '',
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      title: form.title,
      status: form.status,
      visibility: form.visibility,
      role_category: form.role_category,
      position_type: form.position_type,
      work_mode: form.work_mode,
      location: form.location,
      num_openings: Number(form.num_openings) || 1,
      description: form.description,
      stipend: {
        min: form.stipend_min !== '' ? Number(form.stipend_min) : undefined,
        max: form.stipend_max !== '' ? Number(form.stipend_max) : undefined,
        currency: 'INR',
      },
    };
    if (form.application_deadline) payload.application_deadline = form.application_deadline;
    onSave(job._id, payload);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)', borderRadius: 16, padding: '32px',
          width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)', position: 'relative'
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16, background: 'none',
            border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)',
            lineHeight: 1, padding: '4px 8px', borderRadius: 6
          }}
        >×</button>

        <h2 style={{ margin: '0 0 24px', fontSize: 20 }}>Edit Job</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Job Title</label>
            <input className="form-control" value={form.title} onChange={e => set('title', e.target.value)} required maxLength={120} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Visibility</label>
              <select className="form-control" value={form.visibility} onChange={e => set('visibility', e.target.value)}>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-control" value={form.role_category} onChange={e => set('role_category', e.target.value)}>
                {['Engineering','Design','Marketing','Product','Operations','Finance','Sales','Content','Research','Data','Other'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Position Type</label>
              <select className="form-control" value={form.position_type} onChange={e => set('position_type', e.target.value)}>
                {['Full-time','Part-time','Internship','Contract'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Work Mode</label>
              <select className="form-control" value={form.work_mode} onChange={e => set('work_mode', e.target.value)}>
                {['Remote','Hybrid','On-site'].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-control" value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Bangalore" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Stipend Min (₹)</label>
              <input className="form-control" type="number" min="0" value={form.stipend_min} onChange={e => set('stipend_min', e.target.value)} placeholder="0" />
            </div>
            <div className="form-group">
              <label className="form-label">Stipend Max (₹)</label>
              <input className="form-control" type="number" min="0" value={form.stipend_max} onChange={e => set('stipend_max', e.target.value)} placeholder="0" />
            </div>
            <div className="form-group">
              <label className="form-label">Openings</label>
              <input className="form-control" type="number" min="1" value={form.num_openings} onChange={e => set('num_openings', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Application Deadline</label>
            <input className="form-control" type="date" value={form.application_deadline} onChange={e => set('application_deadline', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={5}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              required
              minLength={50}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RecruiterEditModal({ recruiter, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: recruiter.name || '',
    company_name: recruiter.company_name || '',
    phone: recruiter.phone || '',
    company_description: recruiter.company_description || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Edit Recruiter</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>{recruiter.email}</div>
        <form onSubmit={e => { e.preventDefault(); onSave(recruiter._id, form); }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { key: 'name', label: 'Full name', required: true },
              { key: 'company_name', label: 'Company name', required: true },
              { key: 'phone', label: 'Phone' },
            ].map(({ key, label, required }) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{label}{required && <span style={{ color: '#DC2626' }}> *</span>}</label>
                <input
                  type="text"
                  value={form[key]}
                  onChange={e => set(key, e.target.value)}
                  required={required}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
            ))}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Company description</label>
              <textarea
                value={form.company_description}
                onChange={e => set('company_description', e.target.value)}
                rows={3}
                maxLength={600}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdminDashboard({ onLogout }) {
  const [pendingStudents, setPendingStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [verifiedStudents, setVerifiedStudents] = useState([]);
  const [verifiedLoading, setVerifiedLoading] = useState(false);
  const [showVerifiedPanel, setShowVerifiedPanel] = useState(false);
  const [viewedStudent, setViewedStudent] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [allStudentsLoading, setAllStudentsLoading] = useState(false);
  const [showAllStudentsPanel, setShowAllStudentsPanel] = useState(false);

  // Jobs management state
  const [allJobs, setAllJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [showJobsPanel, setShowJobsPanel] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobSaving, setJobSaving] = useState(false);

  // Applicants modal state
  const [applicantsModal, setApplicantsModal] = useState({ job: null, applicants: [], loading: false });

  // Recruiters state
  const [recruiters, setRecruiters] = useState([]);
  const [recruitersLoading, setRecruitersLoading] = useState(false);
  const [showRecruitersPanel, setShowRecruitersPanel] = useState(false);
  const [editingRecruiter, setEditingRecruiter] = useState(null);
  const [recruiterSaving, setRecruiterSaving] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchPendingStudents();
    fetchStats();
  }, []);

  const fetchPendingStudents = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const response = await axios.get(`${API_URL}/api/admin/pending-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingStudents(response.data);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to load students';
      setFetchError(`API error (${err.response?.status || 'network'}): ${msg}`);
      console.error('Error fetching pending students:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVerifiedStudents = async () => {
    setVerifiedLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/verified-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerifiedStudents(response.data);
    } catch (err) {
      showToast('Failed to load verified students', 'error');
    } finally {
      setVerifiedLoading(false);
    }
  };

  const handleVerifiedCardClick = () => {
    const next = !showVerifiedPanel;
    setShowVerifiedPanel(next);
    if (next && verifiedStudents.length === 0) {
      fetchVerifiedStudents();
    }
  };

  const fetchAllStudents = async () => {
    setAllStudentsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/all-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllStudents(response.data);
    } catch (err) {
      showToast('Failed to load all students', 'error');
    } finally {
      setAllStudentsLoading(false);
    }
  };

  const handleAllStudentsCardClick = () => {
    const next = !showAllStudentsPanel;
    setShowAllStudentsPanel(next);
    if (next) fetchAllStudents();
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchAllJobs = async () => {
    setJobsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllJobs(res.data);
    } catch (err) {
      showToast('Failed to load jobs', 'error');
    } finally {
      setJobsLoading(false);
    }
  };

  const handleJobsPanelToggle = () => {
    const next = !showJobsPanel;
    setShowJobsPanel(next);
    if (next && allJobs.length === 0) fetchAllJobs();
  };

  const handleJobSave = async (jobId, payload) => {
    setJobSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/jobs/${jobId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllJobs(prev => prev.map(j => j._id === jobId ? res.data : j));
      setEditingJob(null);
      showToast('Job updated successfully');
    } catch (err) {
      showToast('Error: ' + (err.response?.data?.error || 'Update failed'), 'error');
    } finally {
      setJobSaving(false);
    }
  };

  const openApplicantsModal = async (job) => {
    setApplicantsModal({ job, applicants: [], loading: true });
    try {
      const res = await axios.get(`${API_URL}/api/admin/jobs/${job._id}/applicants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplicantsModal(prev => ({ ...prev, applicants: res.data, loading: false }));
    } catch (err) {
      showToast('Failed to load applicants', 'error');
      setApplicantsModal({ job: null, applicants: [], loading: false });
    }
  };

  const fetchRecruiters = async () => {
    setRecruitersLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/recruiters`, { headers: { Authorization: `Bearer ${token}` } });
      setRecruiters(res.data);
    } catch {
      showToast('Failed to load recruiters', 'error');
    } finally {
      setRecruitersLoading(false);
    }
  };

  const handleRecruitersPanelToggle = () => {
    const next = !showRecruitersPanel;
    setShowRecruitersPanel(next);
    if (next && recruiters.length === 0) fetchRecruiters();
  };

  const handleRecruiterSave = async (id, payload) => {
    setRecruiterSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/recruiters/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setRecruiters(prev => prev.map(r => r._id === id ? res.data : r));
      setEditingRecruiter(null);
      showToast('Recruiter updated');
    } catch (err) {
      showToast('Error: ' + (err.response?.data?.error || 'Update failed'), 'error');
    } finally {
      setRecruiterSaving(false);
    }
  };

  const shareJobOnWhatsApp = (job) => {
    const jobUrl = `${window.location.origin}/jobs/${job._id}`;
    const company = job.company_name || job.posted_by?.company_name || job.posted_by?.name || 'Company';

    // Stipend — handle min-only, max-only, both, none
    const sMin = job.stipend?.min;
    const sMax = job.stipend?.max;
    const fmt = n => Number(n).toLocaleString('en-IN');
    let stipend = null;
    if (sMin && sMax)   stipend = `₹${fmt(sMin)} – ₹${fmt(sMax)}/month`;
    else if (sMin)      stipend = `₹${fmt(sMin)}+/month`;
    else if (sMax)      stipend = `Up to ₹${fmt(sMax)}/month`;

    const deadline = job.application_deadline
      ? new Date(job.application_deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
      : null;

    const metaParts = [job.position_type, job.work_mode];
    if (job.location && job.work_mode !== 'Remote') metaParts.push(job.location);
    if (job.duration_months) metaParts.push(`${job.duration_months} month${job.duration_months > 1 ? 's' : ''}`);

    const skills = Array.isArray(job.skills) && job.skills.length
      ? job.skills.slice(0, 4).join(' · ')
      : null;

    const MONEY  = String.fromCodePoint(0x1F4B0); // 💰
    const PEOPLE = String.fromCodePoint(0x1F465); // 👥
    const CAL    = String.fromCodePoint(0x1F4C5); // 📅
    const TOOLS  = String.fromCodePoint(0x1F6E0); // 🛠
    const POINT  = String.fromCodePoint(0x1F449); // 👉

    const lines = [];
    lines.push(`*${job.title}*`);
    lines.push(`${company}${job.role_category ? ` · ${job.role_category}` : ''}`);
    lines.push('');
    lines.push(metaParts.join(' · '));
    if (stipend)              lines.push(`${MONEY} ${stipend}`);
    if (job.num_openings > 1) lines.push(`${PEOPLE} ${job.num_openings} openings`);
    if (deadline)             lines.push(`${CAL} Apply by ${deadline}`);
    if (skills)               lines.push(`${TOOLS} ${skills}`);
    lines.push('');
    lines.push(`Apply here ${POINT} ${jobUrl}`);

    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleVerify = async (studentId, action) => {
    setActionLoading(studentId + action);
    try {
      await axios.put(`${API_URL}/api/admin/verify-student/${studentId}`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(`Student ${action.toLowerCase()} successfully`);
      setSelectedStudent(null);
      setViewedStudent(null);
      fetchPendingStudents();
      fetchStats();
      if (showAllStudentsPanel) fetchAllStudents();
      if (showVerifiedPanel) fetchVerifiedStudents();
    } catch (err) {
      showToast('Error: ' + (err.response?.data?.error || 'Action failed'), 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => { onLogout(); navigate('/'); };

  return (
    <div className="ad-page">
      {/* Nav */}
      <nav className="ad-nav">
        <span className="ad-nav-brand"><img src={logoMark} alt="" className="brand-mark" />HYRSKILL <span>Admin</span></span>
        <button className="btn btn-secondary btn-sm" onClick={handleLogout}>Sign Out</button>
      </nav>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 76, right: 24, zIndex: 1100 }}>
          <div className={`alert alert-${toast.type}`} style={{ minWidth: 280, boxShadow: 'var(--shadow-md)' }}>
            {toast.msg}
          </div>
        </div>
      )}

      {/* Pending student detail modal */}
      <StudentModal
        student={selectedStudent}
        onClose={() => setSelectedStudent(null)}
        onVerify={handleVerify}
        actionLoading={actionLoading}
      />

      {/* Viewed student modal — read-only for non-pending, actionable for pending */}
      <StudentModal
        student={viewedStudent}
        onClose={() => setViewedStudent(null)}
        onVerify={handleVerify}
        actionLoading={actionLoading}
        readOnly={viewedStudent?.screening_status !== 'Pending'}
      />

      {/* Job edit modal */}
      {editingJob && (
        <JobEditModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSave={handleJobSave}
          saving={jobSaving}
        />
      )}

      {/* Applicants modal */}
      <ApplicantsModal
        job={applicantsModal.job}
        applicants={applicantsModal.applicants}
        loading={applicantsModal.loading}
        onClose={() => setApplicantsModal({ job: null, applicants: [], loading: false })}
      />

      <div className="ad-body">
        {/* Stats */}
        {stats && (
          <div className="ad-stats">
            <div
              className="ad-stat-card"
              onClick={handleAllStudentsCardClick}
              style={{ cursor: 'pointer', outline: showAllStudentsPanel ? '2px solid var(--primary)' : 'none', transition: 'outline 0.15s' }}
              title="Click to view all students"
            >
              <div className="ad-stat-label">Total Students</div>
              <div className="ad-stat-value">{stats.totalStudents}</div>
              <div style={{ fontSize: 11, color: 'var(--primary)', marginTop: 4 }}>
                {showAllStudentsPanel ? 'Hide list ▲' : 'View list ▼'}
              </div>
            </div>
            <div
              className="ad-stat-card"
              onClick={handleVerifiedCardClick}
              style={{ cursor: 'pointer', outline: showVerifiedPanel ? '2px solid var(--primary)' : 'none', transition: 'outline 0.15s' }}
              title="Click to view verified students"
            >
              <div className="ad-stat-label">Verified</div>
              <div className="ad-stat-value green">{stats.verifiedStudents}</div>
              <div style={{ fontSize: 11, color: 'var(--primary)', marginTop: 4 }}>
                {showVerifiedPanel ? 'Hide list ▲' : 'View list ▼'}
              </div>
            </div>
            <div className="ad-stat-card">
              <div className="ad-stat-label">Pending Review</div>
              <div className="ad-stat-value orange">{stats.pendingStudents}</div>
            </div>
          </div>
        )}

        {/* Payments & monetization. Placed above the operational panels:
            revenue and failed payments are what an operator checks first, and
            everything below is unchanged. */}
        <AdminBillingPanel />

        {/* Jobs management panel toggle card */}
        <div
          className="card"
          style={{ marginBottom: 20, cursor: 'pointer', outline: showJobsPanel ? '2px solid var(--primary)' : 'none', transition: 'outline 0.15s' }}
          onClick={handleJobsPanelToggle}
          title="Click to manage all jobs"
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Jobs Management</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                View, edit, and share all job postings
                {allJobs.length > 0 && ` · ${allJobs.length} jobs`}
              </div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 600 }}>
              {showJobsPanel ? 'Hide ▲' : 'Manage ▼'}
            </div>
          </div>
        </div>

        {/* Jobs panel */}
        {showJobsPanel && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="section-header">
              <h2>All Jobs</h2>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {jobsLoading && <div className="spinner" />}
                <button className="btn btn-secondary btn-sm" onClick={fetchAllJobs} disabled={jobsLoading}>
                  Refresh
                </button>
              </div>
            </div>

            {!jobsLoading && allJobs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
                No jobs posted yet.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Company</th>
                      <th>Type</th>
                      <th>Location</th>
                      <th>Applicants</th>
                      <th>Status</th>
                      <th>Posted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allJobs.map(job => (
                      <tr key={job._id}>
                        <td style={{ fontWeight: 600, maxWidth: 200 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={job.title}>
                            {job.title}
                          </div>
                        </td>
                        <td style={{ color: 'var(--text-muted)', maxWidth: 140 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {job.posted_by?.company_name || job.posted_by?.name || '—'}
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ fontSize: 12 }}>{job.position_type}</span>
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · {job.work_mode}</span>
                        </td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{job.location || job.work_mode}</td>
                        <td>
                          <button
                            onClick={() => openApplicantsModal(job)}
                            style={{
                              background: job.applicant_count > 0 ? 'var(--primary)' : 'var(--surface)',
                              color: job.applicant_count > 0 ? '#fff' : 'var(--text-muted)',
                              border: `1px solid ${job.applicant_count > 0 ? 'var(--primary)' : 'var(--border)'}`,
                              borderRadius: 20, padding: '3px 12px', fontSize: 13, fontWeight: 700,
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                            title="View applicants"
                          >
                            {job.applicant_count ?? 0}
                          </button>
                        </td>
                        <td><JobStatusBadge status={job.status} /></td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {new Date(job.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => openApplicantsModal(job)}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              Applicants
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingJob(job)}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm"
                              onClick={() => shareJobOnWhatsApp(job)}
                              style={{
                                background: '#25D366', color: '#fff', border: 'none',
                                borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                                fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                                display: 'flex', alignItems: 'center', gap: 4
                              }}
                              title="Share on WhatsApp"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                              Share
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '10px 0 2px', textAlign: 'center' }}>
                  {allJobs.length} job{allJobs.length !== 1 ? 's' : ''} total
                </div>
              </div>
            )}
          </div>
        )}

        {/* All students panel */}
        {showAllStudentsPanel && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="section-header">
              <h2>All Students</h2>
              {allStudentsLoading && <div className="spinner" />}
            </div>
            {!allStudentsLoading && allStudents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
                No students registered yet.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>College</th>
                      <th>Year</th>
                      <th>Department</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allStudents.map(student => (
                      <tr
                        key={student._id}
                        onClick={() => setViewedStudent(student)}
                        style={{ cursor: 'pointer' }}
                        title="Click to view profile"
                      >
                        <td style={{ fontWeight: 600 }}>{student.name}</td>
                        <td style={{ color: 'var(--text-muted)' }}>{student.email}</td>
                        <td>{student.college || student.iit_campus}</td>
                        <td>{yearLabel(student.year)}</td>
                        <td>{student.department}</td>
                        <td><StatusBadge status={student.screening_status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '10px 0 2px', textAlign: 'center' }}>
                  Click any row to view student profile
                </div>
              </div>
            )}
          </div>
        )}

        {/* Verified students panel */}
        {showVerifiedPanel && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="section-header">
              <h2>Verified Students</h2>
              {verifiedLoading && <div className="spinner" />}
            </div>
            {!verifiedLoading && verifiedStudents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
                No verified students yet.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>College</th>
                      <th>Year</th>
                      <th>Department</th>
                    </tr>
                  </thead>
                  <tbody>
                    {verifiedStudents.map(student => (
                      <tr
                        key={student._id}
                        onClick={() => setViewedStudent(student)}
                        style={{ cursor: 'pointer' }}
                        title="Click to view profile"
                      >
                        <td style={{ fontWeight: 600 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            {student.name}
                            <span style={{ background: '#d1fae5', color: '#065f46', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>Verified</span>
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-muted)' }}>{student.email}</td>
                        <td>{student.college || student.iit_campus}</td>
                        <td>{yearLabel(student.year)}</td>
                        <td>{student.department}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '10px 0 2px', textAlign: 'center' }}>
                  Click any row to view student profile
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recruiters management toggle card */}
        <div
          className="card"
          style={{ marginBottom: 20, cursor: 'pointer', outline: showRecruitersPanel ? '2px solid var(--primary)' : 'none', transition: 'outline 0.15s' }}
          onClick={handleRecruitersPanelToggle}
          title="Click to manage recruiters"
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Recruiters</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
                View and edit recruiter profiles
                {recruiters.length > 0 && ` · ${recruiters.length} recruiters`}
              </div>
            </div>
            <div style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 600 }}>
              {showRecruitersPanel ? 'Hide ▲' : 'Manage ▼'}
            </div>
          </div>
        </div>

        {/* Recruiters panel */}
        {showRecruitersPanel && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="section-header">
              <h2>All Recruiters</h2>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {recruitersLoading && <div className="spinner" />}
                <button className="btn btn-secondary btn-sm" onClick={fetchRecruiters} disabled={recruitersLoading}>Refresh</button>
              </div>
            </div>
            {!recruitersLoading && recruiters.length === 0 && (
              <p style={{ color: 'var(--text-muted)', fontSize: 14, padding: '20px 0' }}>No recruiters found.</p>
            )}
            {recruiters.map(r => (
              <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--primary)', fontSize: 16, flexShrink: 0 }}>
                  {(r.company_name || r.name || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{r.company_name || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.name} · {r.email}{r.phone ? ` · ${r.phone}` : ''}</div>
                  {r.company_description && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.company_description}</div>}
                </div>
                <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); setEditingRecruiter({ ...r }); }}>Edit</button>
              </div>
            ))}
          </div>
        )}

        {/* Recruiter edit modal */}
        {editingRecruiter && (
          <RecruiterEditModal
            recruiter={editingRecruiter}
            saving={recruiterSaving}
            onSave={handleRecruiterSave}
            onClose={() => setEditingRecruiter(null)}
          />
        )}

        {/* Pending table */}
        <div className="card">
          <div className="section-header">
            <h2>Pending Verification</h2>
            {loading && <div className="spinner" />}
          </div>

          {fetchError ? (
            <div className="alert alert-error" style={{ margin: '16px 0' }}>
              {fetchError}
              <div style={{ fontSize: 12, marginTop: 6, opacity: 0.8 }}>
                Token in use: {token ? token.slice(0, 30) + '…' : 'NONE'}
              </div>
            </div>
          ) : pendingStudents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ margin: '0 auto 16px', display: 'block', opacity: 0.4 }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <p style={{ fontWeight: 600 }}>No pending students</p>
              <p style={{ fontSize: 14, marginTop: 4 }}>All profiles have been reviewed.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>College</th>
                    <th>Year</th>
                    <th>Department</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingStudents.map(student => (
                    <tr
                      key={student._id}
                      onClick={() => setSelectedStudent(student)}
                      style={{ cursor: 'pointer' }}
                      title="Click to view full details"
                    >
                      <td style={{ fontWeight: 600 }}>{student.name}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{student.email}</td>
                      <td>{student.college || student.iit_campus}</td>
                      <td>{yearLabel(student.year)}</td>
                      <td>{student.department}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleVerify(student._id, 'Verified')}
                            disabled={actionLoading === student._id + 'Verified'}
                          >
                            {actionLoading === student._id + 'Verified' ? '…' : 'Approve'}
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleVerify(student._id, 'Rejected')}
                            disabled={actionLoading === student._id + 'Rejected'}
                          >
                            {actionLoading === student._id + 'Rejected' ? '…' : 'Reject'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '10px 0 2px', textAlign: 'center' }}>
                Click any row to view full student details
              </div>
            </div>
          )}
        </div>

        {/* Guide */}
        <div className="card" style={{ marginTop: 20 }}>
          <h3 style={{ marginBottom: 14 }}>Verification Guide</h3>
          <ul style={{ lineHeight: 2.2, color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18 }}>
            <li>Verify that the email domain matches the claimed college</li>
            <li>Check that department and year information is plausible</li>
            <li>Click a student row to view their full profile — bio, skills, resume, and links</li>
            <li>Approve genuine students to make their profiles searchable by recruiters</li>
            <li>Reject profiles that appear fraudulent or incomplete</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
